import axios from 'axios';
import localLensData from '../data/lens-database.json';

/**
 * DatabaseService handles the lens database and recommendations.
 * Mapped to the new 9-question schema.
 */
class DatabaseService {
  constructor() {
    this.databaseFile = 'lens-database.json';
    this.database = null;
    this.lastUpdated = null;
  }

  async initialize() {
    if (this.database) return this.database;
    
    // Load from local JSON (or localStorage if cached)
    const storedData = localStorage.getItem(this.databaseFile);
    if (storedData) {
      const parsed = JSON.parse(storedData);
      this.database = parsed.data;
      return this.database;
    }

    // Fallback to local import if nothing in storage
    if (localLensData?.data) {
      this.database = this.transformLensData(localLensData.data);
      localStorage.setItem(this.databaseFile, JSON.stringify({
        data: this.database,
        lastUpdated: new Date()
      }));
    }
    return this.database;
  }

  /**
   * Internal transform to ensure UI consistency
   */
  transformLensData(data) {
    return data.map(lens => ({
      id: lens.id || Math.random().toString(36).substr(2, 9),
      name: lens.name || 'Unknown Lens',
      manufacturer: lens.manufacturer || 'Unknown',
      type: (lens.type || 'monofocal').toLowerCase(),
      description: lens.description || '',
      price: {
        category: lens.price_category?.toLowerCase() || 'premium',
        range: lens.price_range || '$2,500 - $4,000',
        coveredByInsurance: lens.insurance_coverage || 'Partially'
      },
      visionRanges: {
        distance: this.valToBool(lens.vision_ranges?.distance),
        intermediate: this.valToBool(lens.vision_ranges?.intermediate),
        near: this.valToBool(lens.vision_ranges?.near)
      },
      artifactProfile: (lens.artifact_profile || 'minimal').toLowerCase(),
      suitableFor: Array.isArray(lens.suitable_activities) ? lens.suitable_activities : [],
      suitableForConditions: Array.isArray(lens.suitable_medical_conditions) ? lens.suitable_medical_conditions : [],
      specialFeatures: Array.isArray(lens.special_features) ? lens.special_features : [],
      prosAndCons: {
        pros: Array.isArray(lens.pros) ? lens.pros : [],
        cons: Array.isArray(lens.cons) ? lens.cons : []
      },
      manufacturerUrl: lens.lens_url || '#'
    }));
  }

  valToBool(val) {
    if (!val) return false;
    const s = String(val).toLowerCase();
    return s.includes('excellent') || s.includes('good') || s.includes('yes') || s.includes('moderate');
  }

  /**
   * NEW SCORING ENGINE (based on 9-question schema)
   */
  getLensRecommendations(answers) {
    if (!this.database) return [];

    // Map database lenses into categories for scoring
    const scoredLenses = this.database.map(lens => {
      let score = 0;
      const type = lens.type.toLowerCase();
      
      // Category flags
      const isMonofocal = type.includes('monofocal');
      const isEDOF = type.includes('edof') || type.includes('depth of focus');
      const isMultifocal = type.includes('multifocal') || type.includes('trifocal');
      const isLAL = lens.name.toLowerCase().includes('lal') || lens.name.toLowerCase().includes('light adjustable');
      const isToric = type.includes('toric');

      // Q1: Visual Priorities
      const cd = answers.criticalDistances || {};
      if (cd.near) {
        if (isMultifocal) score += 4;
        if (isEDOF) score += 2;
      }
      if (cd.intermediate) {
        if (isEDOF) score += 4;
        if (isMultifocal) score += 2;
      }
      if (cd.distance) {
        if (isMonofocal || isEDOF || isLAL) score += 2;
      }

      // Q2: Reading Habits
      if (answers.readingHabits === 'avid' && isMultifocal) score += 5;
      if (answers.readingHabits === 'occasional' && isEDOF) score += 3;

      // Q3: Glasses Preference
      if (answers.glassesPreference === 'strongly_dislike') {
        if (isMultifocal) score += 5;
        if (isEDOF) score += 3;
      } else if (answers.glassesPreference === 'fine' && isMonofocal) {
        score += 5;
      }

      // Q4: Night Driving
      if (answers.nightDriving === 'frequent_intolerant') {
        if (isMonofocal || isLAL) score += 5;
        if (isMultifocal) score -= 4;
      }

      // Q5: Personality
      if (answers.personality === 'perfectionist') {
        if (isLAL) score += 5;
        if (isMonofocal) score += 2;
      }

      // Q6: Eye Conditions
      const ec = answers.eyeConditions || {};
      const hasCondition = ec.amd || ec.glaucoma || ec.dryEye || ec.corneal;
      if (hasCondition) {
        if (isMonofocal || isLAL) score += 6;
        if (isMultifocal) score -= 10;
        if (isEDOF) score -= 3;
      }

      // Q7: Prior Surgery
      if (answers.priorSurgery === 'yes' && isLAL) score += 8;

      // Q8: Astigmatism
      if (answers.astigmatism === 'yes' && isToric) score += 10;

      // Q9: Budget
      if (answers.budget === 'insurance_only') {
        if (isMonofocal) score += 5;
        if (!isMonofocal) score -= 5;
      }

      return {
        ...lens,
        score,
        matchPercentage: Math.min(100, Math.max(0, (score / 25) * 100)) // Scaled
      };
    });

    return scoredLenses
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }

  getMockLensData() {
    return this.database?.[0] || null;
  }
}

export default new DatabaseService();
